/**
 * This component manage the import workflow for CSV files.
 *
 * TODO right now this workflow is bugged for several reason:
 * During the workflow, once the table metadata are registrer on the backend, we redirect from /cellar/fileImport
 * to /cellar/fileImport/:tableName
 * This trigger `Cellar.tsx` and its children to be re-render which erase their state and the isNewTable Provider.
 * This means that we cannot use them to detect the current upload state of the file.
 * Therefor we have to rely on tricks to understand in which state the file/table we are looking is in right now.
 *
 * A better solution would be to use a context which is a parent of the router instead.
 */

import React, { useEffect, useState, useMemo, useContext } from 'react';
import _ from 'lodash';
import { useQueryClient } from '@tanstack/react-query';
import { translateBlock } from 'i18n';
import { useTranslation } from 'react-i18next';

import Loading from 'components/Loading';
import HeaderLayout from 'ui/HeaderLayout/HeaderLayout';
import { PreviewData } from 'routes/Cellar/types';
import { Step } from 'routes/ImportCsv/types';

import { trackEvent } from 'helpers/trackerHelpers';
import { styled } from '@mui/material/styles';
import { getTime } from 'date-fns';
import Flex from 'ui/Flex/Flex';
import Forbidden from 'components/Errors/Forbidden';
import { useNavigate, useParams } from 'react-router-dom';
import NewTableContext from 'routes/Cellar/context/NewTable/NewTableContext';
import PrevizTableStep from './components/PrevizTableStep/PrevizTableStep';
import SelectFileStep from './components/SelectFileStep/SelectFileStep';
import ShowTableStep from './components/ShowTableStep/ShowTableStep';
import { useNotifyCSVMessage } from './hooks/csvSnackbar';
import { UploadedFileT, useUploadedFiles } from './hooks/uploadedFiles';
import { useCanAccessCSVData } from './hooks/scopes';
import { useTableDataQuery } from './hooks/getData';
import {
    useTableDataMutation,
    useDropTableMutation,
} from './hooks/tableMetadata';
import { TableT } from 'routes/Cellar/components/DomainDetails';

/**
 * A regexp that match almost all Timezone format.
 */
const TZ_EXP = new RegExp(
    /Z$|(GMT|UTC)?(\+|-)\d{1,2}(:\d{1,2})?$|(GMT|UTC)$/,
    'i',
);

type Props = {
    // null case corresponds to new table import
    parentRef: React.RefObject<HTMLDivElement>;
    initialStep: Step;
    // rights of the connected user on the selected table
    tableMetadata?: TableT;
};

const StyImportWorflowContainer = styled('div', {
    name: 'StyImportWorflowContainer',
})({
    backgroundColor: 'white',
    borderLeft: '1px solid #efefef',
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
});

const ImportWorkflow = ({
    tableMetadata,
    parentRef,
    initialStep,
}: Props): React.ReactElement => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { table: selectedTable = '' } = useParams();
    const { isNewTable, setIsNewTable } = useContext(NewTableContext);

    const rightsOnTable = tableMetadata?.rights || [];
    const title = isNewTable
        ? t('importCSV.addingNewTableFile')
        : translateBlock(selectedTable);

    const errorSnackbarNotifCallback = useNotifyCSVMessage('error');
    const warningSnackbarNotifCallback = useNotifyCSVMessage('warning');
    const successSnackbarNotifCallback = useNotifyCSVMessage('success');

    const [step, setStep] = useState<Step>(initialStep);
    const [isLastUploadRunning, setIsLastUploadRunning] = useState<boolean>(
        step === 'showTableStep',
    );
    const [shouldFetchTableData, setShouldFetchTableData] = useState(false);

    const onGetLastUploadedFileSuccess = (data: UploadedFileT) => {
        if (data.uploadStatus !== 'running') {
            if (
                data.uploadStatus === 'failed' ||
                (data.failedData && data.failedData.length > 0)
            ) {
                warningSnackbarNotifCallback({
                    message: t('importCSV.error.rowFailures'),
                });
            }
            setIsNewTable(false);
            setIsLastUploadRunning(false);
            setShouldFetchTableData(true);
            return;
        }

        /* TODO BEWARE
         * Due to a bug in the backend side of thing, some upload are stuck forever in the `running` state.
         * This is a **VERY** hacky workaround. We assume that if the upload date is >1 minutes,
         * it is probably stuck and we stop there and still show the table.
         * One **BIG** issue is that the date that we receive has no Timezone indication.
         * This code assume that the date that we get is from UTC-0/GMT (this is why we add `Z` at the end of the date).
         * **It will break if the date is not at UTC-0**.
         * This code is really not meant to stay and a proper fix should be found ASAP.
         */
        const now = new Date();
        let uploadDate = new Date(data.uploadDate);
        if (!data.uploadDate.match(TZ_EXP)) {
            uploadDate = new Date(`${data.uploadDate}Z`);
        }
        const tNow = getTime(now);
        const tUp = getTime(uploadDate);
        const diff = (tNow - tUp) / 60000.0;
        if (diff > 1) {
            setIsNewTable(false);
            setIsLastUploadRunning(false);
            setShouldFetchTableData(true);
            warningSnackbarNotifCallback({
                message: t('importCSV.error.runningForTooLong'),
            });
        }
    };

    useEffect(() => {
        // TODO StrictNullCheck : Try to remove "!"
        parentRef.current!.scrollTo(0, 0);
    }, [parentRef, step]);

    const [previzData, setPrevizData] = useState<PreviewData>({
        columns: [],
        rows: [],
        name: '',
        nbRows: 0,
    });
    const [rawfile, setRawfile] = useState<File>();

    const accessRestrictedNotifCallback = (error: { message: string }) => {
        errorSnackbarNotifCallback(error);
        navigate('/cellar/filesImport');
    };

    // Checks if the user has access to the resource
    const canAccessCSVData = useCanAccessCSVData(selectedTable);
    // if the selectedTable cannot be fully accessed, we raise an error.
    if (!canAccessCSVData) {
        accessRestrictedNotifCallback({
            message: t('importCSV.error.noAccessToCsv'),
        });
    }

    // NOTE - we know the table data is ready when the last upload has finished running, hence the `enabled` condition
    // TODO: handle errors
    const {
        data: tableData,
        isFetching: isTableDataFetching,
        error: queryTableError,
    } = useTableDataQuery(selectedTable, {
        initialData: { data: [], columns: [] },
        enabled:
            shouldFetchTableData &&
            canAccessCSVData &&
            step === 'showTableStep' &&
            !!selectedTable,
    });

    // TODO SMELL: This is also not very glop.
    // This is a quick-fix to emulate the onError callback from useQuery since the migration to
    // react-query v5.
    // There is probably a better way to do this.
    useEffect(() => {
        if (queryTableError) {
            accessRestrictedNotifCallback(queryTableError);
        }
    }, [queryTableError, accessRestrictedNotifCallback]);

    // TODO SMELL: using useQuery+useEffect for state syncing is a bad practice.
    // We should refactor the code to avoid it.
    useEffect(() => {
        if (tableData) {
            setShouldFetchTableData(false);
        }
    }, [tableData, setShouldFetchTableData]);

    // NOTE - the uploads are processed asynchronously by the backend, and we must wait until
    // those async processes are completed in order to fetch the latest data. In order to know
    // whether the latest upload processing is done we poll the uploads endpoint and check
    // for the latest upload status. However we only poll when the latest upload is running,
    // in order to avoid useless requests.
    const { data: latestUploadedFile } = useUploadedFiles(selectedTable, {
        refetchInterval: isLastUploadRunning ? 2000 : false,
        enabled: step === 'showTableStep',
    });

    // TODO SMELL: using useQuery+useEffect for state syncing is a bad practice.
    // We should refactor the code to avoid it.
    useEffect(() => {
        if (latestUploadedFile) {
            onGetLastUploadedFileSuccess(latestUploadedFile);
        }
    }, [latestUploadedFile, onGetLastUploadedFileSuccess]);

    const useDropTable = useDropTableMutation(
        queryClient,
        selectedTable,
        (content) => {
            successSnackbarNotifCallback(content);
            navigate('/cellar/filesImport');
        },
        errorSnackbarNotifCallback,
    );
    const dropTable = () => {
        trackEvent(
            'settings/data/csv-imports-management/nouvelle-table/csv-déposé',
        );
        useDropTable.mutate({});
    };

    const useTableUpdateFile = useTableDataMutation(
        queryClient,
        selectedTable,
        () => {
            setStep('showTableStep');
            setIsLastUploadRunning(true);
        },
        errorSnackbarNotifCallback,
    );

    const hasFailedDataOnLastUpload = useMemo(
        () =>
            latestUploadedFile &&
            !_.isEmpty(latestUploadedFile) &&
            latestUploadedFile.failedData.length > 0,
        [latestUploadedFile],
    );

    if (isLastUploadRunning || isTableDataFetching) {
        let message = null;
        if (isLastUploadRunning) {
            message = isNewTable
                ? t('importCSV.importLoadingNewTable')
                : t('importCSV.importLoading');
        }
        return (
            <Flex justify="center" sx={{ flexGrow: 1 }}>
                <Loading message={message} fullWidth />
            </Flex>
        );
    }

    const canWrite = rightsOnTable.includes('write');
    const canRead = canWrite || rightsOnTable.includes('read');

    if (selectedTable && rightsOnTable && !canRead) {
        return <Forbidden />;
    }

    return (
        <StyImportWorflowContainer>
            <HeaderLayout title={title} />

            <Flex direction="column" sx={{ p: '2rem', flexGrow: 1 }}>
                {step === 'showTableStep' && (
                    <ShowTableStep
                        tableData={tableData}
                        tableMetadata={tableMetadata}
                        latestUploadedFile={latestUploadedFile}
                        hasFailedDataOnLastUpload={!!hasFailedDataOnLastUpload}
                        selectedTable={selectedTable}
                        setStep={setStep}
                        canEditRights={canWrite}
                        dropTable={dropTable}
                    />
                )}
                {step === 'selectFileStep' && (
                    <SelectFileStep
                        selectedTable={selectedTable}
                        initialStep={initialStep}
                        setStep={setStep}
                        // TODO StrictNullCheck : Try to remove "!"
                        tableData={tableData!}
                        isNewTable={isNewTable}
                        setRawfile={setRawfile}
                        setPrevizData={setPrevizData}
                    />
                )}
                {step === 'previzTableStep' && (
                    <PrevizTableStep
                        previzData={previzData}
                        setStep={setStep}
                        tableColumns={tableData!.columns}
                        setIsLastUploadRunning={setIsLastUploadRunning}
                        useTableUpdateFile={useTableUpdateFile}
                        rawfile={rawfile}
                    />
                )}
            </Flex>
        </StyImportWorflowContainer>
    );
};

export default ImportWorkflow;
